/**
 * PATH: packages/ui/src/kanban/components/card-detail/commentAuthor.ts
 * FILE_ID: UI-KANBAN-COMMENT-AUTHOR-001
 * PURPOSE: One place that turns a card-note author into a human label.
 * WHY: Card notes rendered the author as a raw `User #18` id while the
 *      Teams thread showed the real name (BUG-2026-07-KANBAN-CARD-NOTE-
 *      AUTHOR-ID). This reuses the SAME resolution threads rely on — the
 *      server-provided display name (CommentSerializer.author_name, which
 *      mirrors the thread's get_full_name → email local-part) — and only
 *      falls back to the org member directory, NEVER to the numeric id.
 * FLOW:
 *   comment.author_name ─┐
 *   member directory ────┼─▶ human label ("System" when unauthored)
 *   (never "User #<id>") ┘
 * RELATED:
 *   - CommentsSection.tsx / KanbanCardPeekComments.tsx: the two note feeds.
 *   - serializers_collab.py CommentSerializer.author_name: the source field.
 */
import type { KanbanMember } from '../../api'
import type { CardCommentT } from '../../types'

/**
 * Resolve a card-note author to a display label.
 *
 * @param comment       the note (only `author` + `author_name` are read)
 * @param resolveMember optional lookup into the org member directory —
 *                      the SAME `KanbanMember` directory the Teams thread
 *                      uses for @mention resolution
 * @returns a human label; `"System"` for an unauthored note; a graceful
 *          `"Teammate"` when neither the server name nor the directory can
 *          name the author — but never a raw `User #<id>`.
 */
export function resolveCommentAuthorLabel(
  comment: Pick<CardCommentT, 'author' | 'author_name'>,
  resolveMember?: (id: number) => KanbanMember | undefined,
): string {
  const serverName = comment.author_name?.trim()
  if (serverName) return serverName
  if (comment.author == null) return 'System'
  const member = resolveMember?.(comment.author)
  const memberName = (member?.name || '').trim() || (member?.email || '').trim()
  return memberName || 'Teammate'
}
